import { FlattenedItem, TreeNode } from '@/common/components/Tree/TreeItemView/TreeItemView.types';

export function getParents<T extends TreeNode<T>>(items: T[], id: StringOrNumber): T[] {
  return getPathToItem(items, id)?.slice(0, -1) ?? [];
}

export function setProperty<T extends Partial<TreeNode<T>>, K extends keyof T>(
  items: T[],
  id: StringOrNumber,
  property: K,
  setter: (value: T[K]) => T[K],
): T[] {
  for (const item of items) {
    if (item.id === id) {
      item[property] = setter(item[property]);

      continue;
    }

    if (item.children?.length) {
      item.children = setProperty(item.children, id, property, setter);
    }
  }

  return [...items];
}

export function recursiveSetProperty<T extends Partial<TreeNode<T>>, K extends keyof T>(
  items: T[],
  property: K,
  value: T[K],
) {
  for (const item of items) {
    item[property] = value;

    if (item.children?.length) {
      item.children = recursiveSetProperty(item.children, property, value);
    }
  }

  return [...items];
}

export function updateItem<T extends Partial<TreeNode<T>>>(items: T[], updated: T) {
  const res: T[] = [];

  for (const item of items) {
    if (item.id === updated.id) {
      res.push(updated);
    } else {
      if (item.children?.length) {
        item.children = updateItem(item.children, updated);
      }

      res.push(item);
    }
  }

  return res;
}

export function removeItem<T extends Partial<TreeNode<T>>>(items: T[], id: StringOrNumber) {
  const newItems = [];

  for (const item of items) {
    if (item.id === id) {
      continue;
    }

    if (item.children) {
      item.children = removeItem(item.children, id);
    }

    newItems.push(item);
  }

  return newItems;
}

export function getPathToItem<T extends TreeNode<T>>(items: T[], id: StringOrNumber): Nullable<T[]> {
  for (const item of items) {
    if (item.id === id) {
      return [item];
    }

    if (item.children) {
      const childPath = getPathToItem(item.children, id);

      if (childPath) {
        return [item, ...childPath];
      }
    }
  }

  return null;
}

export function collectSubtreeIds<T extends TreeNode<T>>(items: T[], rootId: StringOrNumber): StringOrNumber[] {
  const root = findItemDeep(items, rootId);

  return root ? collectIds([root]) : [];
}

function collectIds<T extends TreeNode<T>>(items: T[]): StringOrNumber[] {
  return items.flatMap((item) => [item.id, ...collectIds(item.children ?? [])]);
}

export function findItemDeep<T extends TreeNode<T>>(items: T[], id: StringOrNumber): T | undefined {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }

    if (item.children) {
      const child = findItemDeep(item.children, id);

      if (child) {
        return child;
      }
    }
  }

  return undefined;
}

export function getParentId<T extends TreeNode<T>>(
  depth: number,
  previousItem: FlattenedItem<T> | undefined,
  newItems: FlattenedItem<T>[],
  overItemIndex: number,
): Nullable<StringOrNumber> {
  if (depth === 0 || !previousItem) {
    return null;
  }

  if (depth === previousItem.depth) {
    return previousItem.parentId;
  }

  if (depth > previousItem.depth) {
    return previousItem.id;
  }

  const newParent = newItems
    .slice(0, overItemIndex)
    .reverse()
    .find((item) => item.depth === depth)?.parentId;

  return newParent ?? null;
}
